import { MouseEvent, ReactElement, useCallback, useEffect, useRef } from 'react';
import { Sidebar } from '@components/common';
import { NavLink, useLocation, useMatches, useNavigate } from 'react-router-dom';
import { overlayAddFadeAction } from '@components/common/sort';
import { useSiderToggleMenu } from '@store/useSiderToggleMenu';
import { useRefetchAllData } from '@helper/fetchData';
import { dashboardRedirectPaths, presetRoutePath, resyncRoutePath } from '@components/common/header/index';
import { useProductSamplingStore } from '@store/productSamplingStore';

export type IHeaderProps = {
	subRoute?: boolean;
	menuHeading?: string;
};
export const Header = (props: IHeaderProps): ReactElement => {
	const currentBackIndex = useRef<number>(0);
	const currentRouteName = useRef<string>('');
	const { setModal, pageRefetch } = useSiderToggleMenu();
	const refreshButtonRef = useRef<HTMLButtonElement>(null);
	const { refetchAllData } = useRefetchAllData();
	const { subRoute, menuHeading } = props;
	const navigate = useNavigate();
	const matches = useMatches();
	const { pathname, state: locationState } = useLocation();
	const routeTitle = [...matches]
		.reverse()
		.map((match) => (match.handle as { title?: string } | undefined)?.title)
		.find((title): title is string => typeof title === 'string');
	const isDashboard = pathname === '/';

	useEffect(() => {
		if (currentRouteName.current === menuHeading) return;

		if (presetRoutePath.find((path) => path.currentPath === pathname)) {
			currentBackIndex.current++;
			currentRouteName.current = menuHeading ?? '';
		}
	}, [menuHeading, pathname]);

	const goBack = (): void => {
		const site360Back = useProductSamplingStore.getState().site360ReturnState;
		if (site360Back && pathname.startsWith('/product-sampling-list')) {
			useProductSamplingStore.getState().setSite360ReturnState(null);
			navigate('/site-details-rr', {
				state: {
					id: site360Back.id,
					app_id_c: site360Back.app_id_c,
					is_dr_required: site360Back.is_dr_required,
				},
			});
			return;
		}
		if (dashboardRedirectPaths.includes(pathname)) {
			navigate('/');
			return;
		}

		// Opened from Site 360 with `state.siteId` — back should return to that site, not browser history.
		if (pathname === '/stakeholder-mapping') {
			const siteId =
				(locationState as { siteId?: string | number; id?: string | number } | null)?.siteId ??
				(locationState as { siteId?: string | number; id?: string | number } | null)?.id;
			if (siteId != null && String(siteId).trim() !== '') {
				navigate('/site-details-rr', { state: { id: siteId }, replace: true });
				currentBackIndex.current = 0;
				currentRouteName.current = '';
				return;
			}
		}

		// Add User from stakeholder mapping sets `returnTo` — back should resume mapping, not `/leads`.
		if (pathname === '/lead/basic-details') {
			const returnTo = (locationState as { returnTo?: string } | null)?.returnTo?.trim();
			if (returnTo?.startsWith('/stakeholder-mapping')) {
				try {
					const url = new URL(returnTo, window.location.origin);
					const siteId = url.searchParams.get('siteId') ?? url.searchParams.get('id');
					if (siteId) {
						navigate('/stakeholder-mapping', { state: { siteId }, replace: true });
						currentBackIndex.current = 0;
						currentRouteName.current = '';
						return;
					}
				} catch {
					// If parsing fails, fall back to the original navigation target.
				}
				navigate(returnTo, { replace: true });
				currentBackIndex.current = 0;
				currentRouteName.current = '';
				return;
			}
		}

		// Opened from calendar — pop history so we restore the original calendar entry
		// (navigate('/calendar', { replace: true }) would leave a duplicate and require two backs).
		const calendarReturnTo = (locationState as { returnTo?: string } | null)?.returnTo?.trim();
		if (calendarReturnTo === '/calendar') {
			navigate(-1);
			currentBackIndex.current = 0;
			currentRouteName.current = '';
			return;
		}

		const preset = presetRoutePath.find((p) => p.currentPath === pathname);
		if (preset) {
			const steps = currentBackIndex.current;
			// After refresh or replace/redirect into this screen, refs are 0 but
			// navigate(-0) is a no-op; use the configured list parent instead.
			if (steps > 0) {
				if (preset.previousPath) {
					navigate(preset.previousPath);
				} else {
					navigate(-steps);
				}
			} else if (window.history.length > 1) {
				// Prefer real history back so the previous entry keeps its location.state
				// (e.g. club id on /dfcc-clubs/club-details after visiting transfer-member).
				navigate(-1);
			} else {
				navigate(preset.previousPath);
			}
			currentBackIndex.current = 0;
			currentRouteName.current = '';
			return;
		}

		// Short stacks (new tab, some redirects): avoid navigate(-1) leaving the app.
		if (window.history.length <= 2) {
			navigate('/');
			return;
		}
		navigate(-1);
	};

	const refetchSync = useCallback(
		(e: MouseEvent<HTMLButtonElement>): void => {
			e.preventDefault();
			// If a page-specific refetch is registered for this route, use it
			if (pageRefetch && pageRefetch.pathname === pathname) {
				pageRefetch.fn();
			} else if (pageRefetch === null && pathname === '/') {
				refetchAllData?.('Profile');
				refetchAllData?.('Dropdowns');
			}
			if (refreshButtonRef.current) {
				refreshButtonRef.current.classList.add('spin-animation');
				setTimeout(() => {
					if (refreshButtonRef.current) {
						refreshButtonRef.current.classList.remove('spin-animation');
					}
				}, 3000);
			}
		},
		[refetchAllData, pageRefetch, pathname]
	);

	return (
		<>
			<header className="top-header-wrapper">
				<div className="container">
					<div className="top-header">
						<div className="sidebar-toggle">
							{!subRoute && (
								<button
									type="button"
									aria-label="toggle-button"
									onClick={() => overlayAddFadeAction(() => setModal(true), 'sidebar-overlay')}
									className="navbar-btn"
									value="toggle-button"
								>
									<span className="icon-menu"></span>
								</button>
							)}
							{subRoute && (
								<button
									onClick={goBack}
									type="button"
									id="backButton"
									aria-label="back-button"
									className="navbar-btn hide"
									value="back-button"
								>
									<span className="icon-arrow-left"></span>
								</button>
							)}
						</div>
						<span id="main-header" className="h3">
							{routeTitle ?? menuHeading ?? 'MPower'}
						</span>
						<div className="header-calendar-wrapper">
							{resyncRoutePath.includes(pathname) && (
								<button
									type="button"
									id="resync-button"
									ref={refreshButtonRef}
									aria-label="refresh"
									className="refresh-btn"
									onClick={refetchSync}
								>
									<span className="icon-refresh-cw" />
								</button>
							)}

							{!subRoute && isDashboard && (
								<div className="header-calender">
									<NavLink to="/calendar" aria-label="Go to calender">
										<span className="icon-calendar"></span>
									</NavLink>
								</div>
							)}
						</div>
					</div>
				</div>
			</header>
			<Sidebar />
		</>
	);
};
